/**
 * Helper functions for cloning production orders
 * Shared between orden_produccion.js and orden_activa.js
 */

/**
 * Deep clones orden_produccion data for the clone feature
 * Copies all fields except id, numero, created, updated
 * Resets state fields. Keeps original dates from source order.
 * @param {Object} orden - The source order to clone
 * @returns {Object} Cloned order data ready for form population
 */
export const deepCloneOrdenData = (orden) => {
  if (!orden) return {};

  const cloned = {
    // Core fields
    nombre_producto: orden.nombre_producto,
    cantidad_solicitada: orden.cantidad_solicitada,
    oc: orden.oc,
    numero_reproceso: orden.numero_reproceso,
    tipo_trabajo: orden.tipo_trabajo,

    // Dates - keep original dates from source order
    fecha_inicio: orden.fecha_inicio,
    fecha_entrega: orden.fecha_entrega,
    corte_facturacion: orden.corte_facturacion,

    // Booleans
    nuevo: orden.nuevo,
    reimpresion: orden.reimpresion,
    troquel_n: orden.troquel_n,
    troquel_e: orden.troquel_e,
    trabajo_interno: orden.trabajo_interno,

    // Technical fields
    cabida_preprensa: orden.cabida_preprensa,
    tintas: orden.tintas,
    detalles: orden.detalles,
    cantidad: orden.cantidad,
    sobrantes: orden.sobrantes,
    pliegos: orden.pliegos,
    acabado_general: orden.acabado_general,
    observaciones: orden.observaciones,

    // Relations (IDs can be copied directly)
    cliente: orden.cliente,
    asesor_comercial: orden.asesor_comercial,
    solicitud: orden.solicitud,
    producto: orden.producto,
    material: orden.material,
    medida: orden.medida,
    tipo_papel: orden.tipo_papel,

    // Subprocess parameters (copy ID arrays) - these are simple lookups, just copy IDs
    brillo_interno: orden.brillo_interno || [],
    pegue_interno: orden.pegue_interno || [],
    plastificado_interno: orden.plastificado_interno || [],
    troquelado_interno: orden.troquelado_interno || [],
    acabado_interno: orden.acabado_interno || [],

    // Recubrimiento (copy ID array)
    recubrimiento: orden.recubrimiento || [],

    // State reset - cloned orders start in "editing" state
    activo: true,
    activa: null,
    finalizada: null,

    // Out of scope - set to empty/null (don't copy from source)
    tarea: null,
    notas: [],
    reserva_material: [],
    solicitud_material: [],

    // Explicitly do NOT copy:
    // - id (will be auto-generated)
    // - numero (will be auto-generated by getNextSequenceNumber)
    // - created (will be set by database)
    // - updated (will be set by database)
    // - planeacion (will be cloned separately as nested record)
    // - corte_conversion (will be cloned separately with deep nesting)
  };

  return cloned;
};

/**
 * Clones a planeacion record with all fields and relations
 *
 * @param {Object} planeacion - Source planeacion record with expanded relations
 * @param {Object} pb - PocketBase instance
 * @param {Object} logger - Logger instance
 * @returns {Promise<string|null>} New planeacion ID or null if source is empty
 * @throws {Error} If record creation fails
 */
const clonePlaneacion = async (planeacion, pb, logger) => {
  if (!planeacion) return null;

  const planeacionData = {
    abierto_alto: parseFloat(planeacion.abierto_alto) || 0,
    abierto_ancho: parseFloat(planeacion.abierto_ancho) || 0,
    armado_alto: parseFloat(planeacion.armado_alto) || 0,
    armado_ancho: parseFloat(planeacion.armado_ancho) || 0,
    armado_prof: parseFloat(planeacion.armado_prof) || 0,
    tintas: planeacion.tintas || "",
    lineatura: planeacion.lineatura || "",
    pinza: planeacion.pinza || "",
    troquel_e: Boolean(planeacion.troquel_e),
    troquel_n: Boolean(planeacion.troquel_n),
    numero_troquel: parseInt(planeacion.numero_troquel) || 0,
    cabida_preprensa: planeacion.cabida_preprensa || "",
    tipo_pinza: planeacion.tipo_pinza || "",
    tamano: planeacion.tamano || "",
    parametros_tinta: planeacion.parametros_tinta || []
  };

  try {
    const newPlaneacion = await pb.collection('planeacion').create(planeacionData);
    logger.debug?.("Planeación clonada", {
      originalId: planeacion.id,
      newId: newPlaneacion.id
    });
    return newPlaneacion.id;
  } catch (error) {
    logger.error?.("Error clonando planeación", { planeacion, error });
    throw new Error(`Failed to clone planeacion: ${error.message}`);
  }
};

/**
 * Clones a single impresion_tabla record
 *
 * @param {Object} impresion - Source impresion_tabla record
 * @param {Object} pb - PocketBase instance
 * @param {Object} logger - Logger instance
 * @returns {Promise<string|null>} New impresion_tabla ID or null if source is empty
 * @throws {Error} If record creation fails
 */
const cloneImpresionTabla = async (impresion, pb, logger) => {
  if (!impresion) return null;

  const impresionData = {
    impresos: parseInt(impresion.impresos) || 0,
    sobrantes: parseInt(impresion.sobrantes) || 0,
    total: parseInt(impresion.total) || 0,
    pasadas: parseInt(impresion.pasadas) || 0,
    tinta_color: impresion.tinta_color || "",
    montaje: impresion.montaje || "",
    tamano: impresion.tamano || "",
    parametros_tinta: impresion.parametros_tinta || []
  };

  try {
    const newImpresion = await pb.collection('impresion_tabla').create(impresionData);
    logger.debug?.("Impresión tabla clonada", {
      originalId: impresion.id,
      newId: newImpresion.id
    });
    return newImpresion.id;
  } catch (error) {
    logger.error?.("Error clonando impresion_tabla", { impresion, error });
    throw new Error(`Failed to clone impresion_tabla: ${error.message}`);
  }
};

/**
 * Clones corte_conversion_tabla records with nested impresion_tabla
 *
 * @param {Array<Object>} parametroArray - Array of corte_conversion_tabla records
 * @param {Object} pb - PocketBase instance
 * @param {Object} logger - Logger instance
 * @returns {Promise<Array<string>>} Array of new corte_conversion_tabla IDs
 * @throws {Error} If any record creation fails
 */
const cloneCorteConversionTabla = async (parametroArray, pb, logger) => {
  if (!Array.isArray(parametroArray) || parametroArray.length === 0) {
    return [];
  }

  const parametroIds = [];

  for (const param of parametroArray) {
    try {
      // Clone nested impresion_tabla if exists
      let impresionId = null;
      if (param.expand?.impresion) {
        impresionId = await cloneImpresionTabla(param.expand.impresion, pb, logger);
      }

      // Create new corte_conversion_tabla
      const parametroData = {
        cantidad: parseInt(param.cantidad) || 0,
        sobrante: parseInt(param.sobrante) || 0,
        total: parseInt(param.total) || 0,
        tamano_corte: param.tamano_corte || "",
        tamano_cortado: param.tamano_cortado || "",
        detalles: param.detalles || "",
        impresion: impresionId
      };

      const newParametro = await pb.collection('corte_conversion_tabla').create(parametroData);
      parametroIds.push(newParametro.id);

      logger.debug?.("Corte conversion tabla clonado", {
        originalId: param.id,
        newId: newParametro.id,
        hasImpresion: !!impresionId
      });
    } catch (error) {
      logger.error?.("Error clonando corte_conversion_tabla", { param, error });
      throw new Error(`Failed to clone corte_conversion_tabla: ${error.message}`);
    }
  }

  return parametroIds;
};

/**
 * Clones all corte_conversion records with deeply nested parametros and impresion
 *
 * Nesting structure:
 * corte_conversion (papel, parametros[])
 *   └─ corte_conversion_tabla (fields, impresion)
 *       └─ impresion_tabla (fields, parametros_tinta[])
 *
 * @param {Array<Object>} corteArray - Array of corte_conversion records with expanded relations
 * @param {Object} pb - PocketBase instance
 * @param {Object} logger - Logger instance
 * @returns {Promise<Array<string>>} Array of new corte_conversion IDs
 * @throws {Error} If any nested record creation fails
 */
const cloneCorteConversion = async (corteArray, pb, logger) => {
  if (!Array.isArray(corteArray) || corteArray.length === 0) {
    return [];
  }

  const corteConversionIds = [];

  for (const corte of corteArray) {
    try {
      // Clone all nested parametros (corte_conversion_tabla) with their impresion
      const parametroIds = await cloneCorteConversionTabla(
        corte.expand?.parametro || [],
        pb,
        logger
      );

      // Create new corte_conversion with correct schema fields
      const corteData = {
        papel: corte.papel || "",
        parametro: parametroIds
      };

      const newCorte = await pb.collection('corte_conversion').create(corteData);
      corteConversionIds.push(newCorte.id);

      logger.debug?.("Corte conversion clonado", {
        originalId: corte.id,
        newId: newCorte.id,
        parametrosCount: parametroIds.length
      });
    } catch (error) {
      logger.error?.("Error clonando corte_conversion", { corte, error });
      throw new Error(`Failed to clone corte_conversion: ${error.message}`);
    }
  }

  logger.info?.("Todos los corte_conversion clonados", {
    count: corteConversionIds.length
  });

  return corteConversionIds;
};

/**
 * Gets the next sequence number for a new order
 * @param {Object} pb - PocketBase instance
 * @returns {Promise<number>} The next sequence number
 */
const getNextSequenceNumber = async (pb) => {
  let retries = 3;
  while (retries > 0) {
    try {
      let sequence = await pb.collection('orden_sequence').getFirstListItem('');
      if (!sequence) {
        sequence = await pb.collection('orden_sequence').create({ sequence: 1 });
      }
      return sequence.sequence + 1;
    } catch (err) {
      retries--;
      if (retries === 0) throw new Error('No se pudo obtener el número de secuencia');
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
};

/**
 * Creates a direct clone of an order without opening modals
 * This function can be used from any page with access to pb, ordenRepository, and showAlert
 * @param {Object} orden - The order to clone
 * @param {Object} pb - PocketBase instance
 * @param {Object} ordenRepository - The orden repository instance
 * @param {Function} showAlert - Function to show alerts (type, title, message)
 * @param {Function} reloadOrdenes - Function to reload the orders table
 * @param {Function} ordenLogger - Logger function
 * @param {Function} setSaving - Optional function to set saving state
 * @returns {Promise<Object>} The created order
 */
export const handleCloneDirect = async (orden, pb, ordenRepository, showAlert, reloadOrdenes, ordenLogger = console, setSaving = null) => {
  try {
    if (setSaving) setSaving(true);
    ordenLogger.debug?.("Iniciando clonado directo de orden", { ordenId: orden.id });

    // Pre-clone validation
    if (!orden.cliente) {
      showAlert('warning', 'Error de validación', 'La orden no tiene cliente asignado, no se puede clonar');
      if (setSaving) setSaving(false);
      return null;
    }

    // Fetch complete order data with ALL relationships expanded (3 levels deep for corte_conversion)
    const fullOrden = await ordenRepository.getById(orden.id, {
      expand: [
        'cliente',
        'asesor_comercial',
        'solicitud',
        'producto',
        'tipo_papel',
        'material',
        'medida',
        'planeacion',
        'planeacion.parametros_tinta',
        'planeacion.tipo_pinza',
        'planeacion.tamano',
        'corte_conversion',
        'corte_conversion.papel',
        'corte_conversion.parametro',
        'corte_conversion.parametro.impresion',
        'corte_conversion.parametro.impresion.parametros_tinta',
        'brillo_interno',
        'pegue_interno',
        'plastificado_interno',
        'troquelado_interno',
        'acabado_interno',
        'recubrimiento'
      ].join(',')
    });

    ordenLogger.debug?.("Orden completa obtenida para clonar", { ordenId: fullOrden.id });

    // Get next sequence number
    const nextSequence = await getNextSequenceNumber(pb);
    ordenLogger.debug?.("Número de secuencia obtenido", { nextSequence });

    // Clone nested planeacion if it exists (includes parametros_tinta array)
    const planeacionId = await clonePlaneacion(fullOrden.expand?.planeacion, pb, ordenLogger);

    // Clone nested corte_conversion with deep nesting (3 levels: corte → parametro → impresion)
    const corteConversionIds = await cloneCorteConversion(
      fullOrden.expand?.corte_conversion || [],
      pb,
      ordenLogger
    );

    // Deep clone the order data
    const clonedData = deepCloneOrdenData(fullOrden);

    // Prepare the order data for creation
    const orderData = {
      ...clonedData,
      numero: String(nextSequence),
      planeacion: planeacionId,
      corte_conversion: corteConversionIds,
      // subprocess parameters and recubrimiento already in clonedData
      // state fields (activo, activa, finalizada) already set correctly in clonedData
      // out-of-scope fields (tarea, notas, reserva_material, solicitud_material) already set in clonedData
    };

    ordenLogger.debug?.("Creando orden clonada", { numero: orderData.numero });

    // Create the cloned order
    const createdOrden = await ordenRepository.createWithValidation(orderData);

    // Update sequence number in database
    let sequence = await pb.collection('orden_sequence').getFirstListItem('');
    await pb.collection('orden_sequence').update(sequence.id, { sequence: nextSequence });

    ordenLogger.info?.("Orden clonada exitosamente", {
      originalId: orden.id,
      newId: createdOrden.id,
      numero: createdOrden.numero
    });

    // Refresh the orders table to show the new cloned order
    await reloadOrdenes();

    if (setSaving) setSaving(false);
    showAlert('success', 'Orden clonada', `Orden #${createdOrden.numero} creada exitosamente. La orden está en estado "En Edición" y puede editarla desde la tabla.`);

    return createdOrden;
  } catch (error) {
    if (setSaving) setSaving(false);
    showAlert('error', 'Error al clonar', `No se pudo clonar la orden: ${error.message}`);
    ordenLogger.error?.("Error al clonar orden", error);
    return null;
  }
};
